import React from 'react';
import { fromDateOnly } from '../../../../utils/DateOnly';
import { PromotionPreview, PromotionPaymentStatus } from '../../../../model/pointofsale/PromotionPreview';
import { MemberPromotion } from '../../../../model/pointofsale/MemberPromotion';
import { useFormatCurrency } from '../../../../hooks/useFormatters';
import { useTokens } from '../../../../core/theme/TokensProvider';
import { Card } from '../../../../ui/primitives/Card';
import { Stack } from '../../../../ui/primitives/Stack';
import { Text } from '../../../../ui/primitives/Text';
import { Tag } from '../../../../ui/primitives/Tag';
import { Alert } from '../../../../ui/primitives/Alert';

const DATE_FORMAT = 'DD/MM/YYYY';

interface BeforeAfterRowProps {
    label: string;
    before: string;
    after: string;
    changed: boolean;
    'data-testid'?: string;
}

// One "X → Y" line. When nothing changes the arrow is dropped entirely, so the operator's eye is
// only drawn to what the promotion actually moves.
const BeforeAfterRow: React.FC<BeforeAfterRowProps> = ({
    label, before, after, changed, 'data-testid': dataTestId,
}) => {
    const tokens = useTokens();
    return (
        <div
            data-testid={dataTestId}
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: tokens.space[3],
            }}
        >
            <Text variant="body" color="secondary">{label}</Text>
            {changed ? (
                <Text variant="body" weight="medium">
                    <span style={{ color: tokens.color.text.disabled, textDecoration: 'line-through' }}>
                        {before}
                    </span>
                    {' → '}
                    <span style={{ color: tokens.color.warning }}>{after}</span>
                </Text>
            ) : (
                <Text variant="body">{after}</Text>
            )}
        </div>
    );
};

interface PromotionConfirmationPanelProps {
    preview: PromotionPreview;
    memberName: string;
    paymentStatus: PromotionPaymentStatus;
    // A prior application of this same promotion, if any — surfaced as a warning rather than a
    // second confirm dialog on top of this one.
    priorApplication?: MemberPromotion | null;
    // Type-specific extra rows, for a promotion type whose preview carries data this shared panel
    // can't infer. Membership needs none.
    extras?: React.ReactNode;
}

/**
 * What applying a promotion is about to do, shown before anything is committed (FORT-318): the POS
 * line item and total, whether the fee is being taken or recorded as owed, and before → after rows
 * for the membership category and both validity dates.
 *
 * Every value and every "this changes" flag comes from the server's preview response. The panel
 * renders them; it does not recompute them.
 */
export const PromotionConfirmationPanel: React.FC<PromotionConfirmationPanelProps> = ({
    preview,
    memberName,
    paymentStatus,
    priorApplication,
    extras,
}) => {
    const formatCurrency = useFormatCurrency();
    const tokens = useTokens();
    // fromDateOnly, not dayjs — these arrive as UTC midnight via DateReviver, so reading them in
    // local time would render the previous day anywhere west of Greenwich.
    const formatDate = (value: Date) => fromDateOnly(value).format(DATE_FORMAT);

    const sectionHeading = (text: string) => (
        <Text variant="label" color="secondary" weight="semibold">{text}</Text>
    );

    return (
        <Card data-testid="promotion-confirm-panel">
            <Stack gap={5}>
                {priorApplication && (
                    <Alert
                        type="warning"
                        showIcon
                        data-testid="promotion-confirm-reapply-warning"
                        message={`${memberName} has already had "${preview.promotionName}" `
                            + `(last applied ${formatDate(priorApplication.appliedOn)}). `
                            + 'Applying it again will charge the fee a second time.'}
                    />
                )}

                <Stack gap={2}>
                    {sectionHeading('Payment')}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <Text variant="body">{preview.posItemName} × 1</Text>
                        <Text variant="body">{formatCurrency(preview.amount)}</Text>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'baseline',
                            borderTop: `1px solid ${tokens.color.border.default}`,
                            paddingTop: tokens.space[2],
                        }}
                    >
                        <Text variant="body" weight="semibold">Total</Text>
                        <Text
                            variant="h4"
                            weight="bold"
                            data-testid="promotion-confirm-amount"
                        >
                            {formatCurrency(preview.amount)}
                        </Text>
                    </div>
                    <div data-testid="promotion-confirm-payment-status">
                        {paymentStatus === 'Completed'
                            ? <Tag tone="success">Paid now</Tag>
                            : <Tag tone="warning">Unpaid — recorded as owed</Tag>}
                    </div>
                </Stack>

                <Stack gap={2}>
                    {sectionHeading('Membership category')}
                    {preview.categoryChanges && (
                        <Alert
                            type="warning"
                            showIcon
                            data-testid="promotion-confirm-category-change"
                            message={`This changes ${memberName}'s membership category from `
                                + `"${preview.currentMemberCategoryName}" to "${preview.memberCategoryName}".`}
                        />
                    )}
                    <BeforeAfterRow
                        label="Category"
                        before={preview.currentMemberCategoryName}
                        after={preview.memberCategoryName}
                        changed={preview.categoryChanges}
                        data-testid="promotion-confirm-category"
                    />
                </Stack>

                <Stack gap={2}>
                    {sectionHeading('Membership dates')}
                    <BeforeAfterRow
                        label="Start date"
                        before={formatDate(preview.currentValidFrom)}
                        after={formatDate(preview.validFrom)}
                        changed={preview.validFromChanges}
                        data-testid="promotion-confirm-valid-from"
                    />
                    <BeforeAfterRow
                        label="End date"
                        before={formatDate(preview.currentValidTo)}
                        after={formatDate(preview.validTo)}
                        changed={preview.validToChanges}
                        data-testid="promotion-confirm-valid-to"
                    />
                </Stack>

                {extras}
            </Stack>
        </Card>
    );
};

export default PromotionConfirmationPanel;
